import { useInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, Bookmark, BookmarkX } from 'lucide-react';
import { useMe } from '~/api/auth';
import { type SavedItem, savedQueryOptions, useSaveTeamMessage } from '~/api/teamChat';
import { timeOf } from '~/features/chat/chat-format';
import { ChatAvatar } from '~/features/team-chat/ChatAvatar';
import { messageLine, shortWhen } from '~/features/team-chat/chat-labels';
import { notify } from '~/lib/notify';
import { Button } from '~/ui/Button';
import { ErrorNotice } from '~/ui/ErrorNotice';
import { Skeleton } from '~/ui/Skeleton';
import { Hint } from '~/ui/Hint';

export const Route = createFileRoute('/_app/chat/saved')({
  component: SavedPage,
});

/** Saved for later (M6): this person's private list across chats, each with its chat and a jump. */
function SavedPage() {
  const me = useMe();
  const tz = me.person.tz ?? me.workspace.tz;
  const saved = useInfiniteQuery(savedQueryOptions);
  const items = saved.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      <header className="flex items-center gap-2 border-b border-line px-2 py-2 md:px-4">
        <Link
          to="/chat"
          aria-label="Back to chats"
          className="grid size-9 shrink-0 place-items-center rounded-control text-ink-2 hover:bg-paper-2 hover:text-ink md:hidden"
        >
          <ArrowLeft size={18} aria-hidden />
        </Link>
        <Bookmark size={16} aria-hidden className="text-ink-3" />
        <div className="min-w-0 flex-1 leading-tight">
          <h2 className="truncate text-[15px] font-semibold">Saved for later</h2>
          <p className="truncate text-xs text-ink-3">Only you see this list.</p>
        </div>
      </header>
      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto px-3 py-3 md:px-5">
        {saved.isPending ? (
          <Skeleton lines={6} />
        ) : saved.isError ? (
          <ErrorNotice error={saved.error} onRetry={() => saved.refetch()} />
        ) : items.length === 0 ? (
          <p className="py-12 text-center text-sm text-ink-3">
            Nothing saved yet. Save a message from its toolbar to find it here.
          </p>
        ) : (
          <>
            <ul className="mx-auto flex max-w-3xl flex-col gap-1">
              {items.map((it) => (
                <SavedRow key={it.message.id} item={it} tz={tz} />
              ))}
            </ul>
            {saved.hasNextPage && (
              <div className="mx-auto mt-3 flex max-w-3xl justify-center">
                <Button
                  variant="secondary"
                  size="sm"
                  loading={saved.isFetchingNextPage}
                  onClick={() => void saved.fetchNextPage()}
                >
                  Show older
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SavedRow({ item: it, tz }: { item: SavedItem; tz: string }) {
  const m = it.message;
  const unsave = useSaveTeamMessage(m.conversationId);
  const names = {
    person: (id: string) => m.mentions.find((p) => p.id === id)?.name,
    task: (id: string) => m.tasks.find((t) => t.id === id)?.key ?? undefined,
  };
  return (
    <li className="group relative rounded-control border border-line bg-paper">
      <Link
        to="/chat/$id"
        params={{ id: m.conversationId }}
        search={{ seq: m.seq }}
        className="flex items-start gap-3 px-3 py-2.5 pr-11 hover:bg-paper-2"
      >
        <ChatAvatar chat={{ kind: it.chat.kind, title: it.chat.title }} size="sm" />
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline justify-between gap-2">
            <span className="truncate text-xs text-ink-3">
              {it.chat.title} ·{' '}
              <span className="font-medium text-ink-2">{m.author?.name ?? 'Someone'}</span>
            </span>
            <span className="shrink-0 font-mono text-[10.5px] text-ink-3 tabular">
              {shortWhen(m.createdAt, tz)} · {timeOf(m.createdAt, tz)}
            </span>
          </span>
          <span className="line-clamp-3 block text-sm">{messageLine(m, names)}</span>
        </span>
      </Link>
      <Hint label="Remove from saved">
        <button
          type="button"
          aria-label="Remove from saved"
          onClick={() =>
            unsave.mutate(
              { id: m.id, saved: false },
              { onError: (e) => notify.error('Could not remove it.', e) },
            )
          }
          className="absolute top-2 right-2 grid size-8 place-items-center rounded-control text-ink-3 hover:bg-paper-2 hover:text-ink"
        >
          <BookmarkX size={16} aria-hidden />
        </button>
      </Hint>
    </li>
  );
}
